/**
 * Who you are about to talk to.
 *
 * This pane is the reason the widget asks for a name at all. Capturing an
 * identity that no colleague ever sees is data collection with no purpose; the
 * point is that whoever picks a conversation up knows, before typing, whether
 * this is somebody's first visit or their fourth, what page they came in on, and
 * what they asked about last week.
 */
import { Clock, Globe, Mail, Phone, Repeat2, UserRound } from 'lucide-react';
import type { ChatEscalationDto, ChatVisitorCardDto } from '@respectly/shared-types';
import { relativeTime } from './SessionList';

export function VisitorCard({
  visitor,
  escalations,
  onOpenSession,
}: {
  visitor: ChatVisitorCardDto;
  escalations: ChatEscalationDto[];
  onOpenSession: (id: string) => void;
}) {
  const anonymous = !visitor.name && !visitor.email;

  return (
    <div className="space-y-5 p-4">
      <section>
        <div className="flex items-center gap-2">
          <UserRound className="h-4 w-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {visitor.name ?? 'Anonymous visitor'}
          </h2>
        </div>
        {anonymous ? (
          <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
            They have not said who they are. Nothing stops you replying — the
            identity card is an invitation, not a gate.
          </p>
        ) : (
          <dl className="mt-2 space-y-1.5 text-xs">
            {visitor.email && (
              <Row icon={<Mail className="h-3 w-3" />} label="Email">
                <a
                  href={`mailto:${visitor.email}`}
                  className="text-teal-700 hover:underline dark:text-teal-400"
                >
                  {visitor.email}
                </a>
              </Row>
            )}
            {visitor.phone && (
              <Row icon={<Phone className="h-3 w-3" />} label="Phone">
                {visitor.phone}
              </Row>
            )}
            <Row icon={<Repeat2 className="h-3 w-3" />} label="Visits">
              {visitor.sessionCount === 1
                ? 'First conversation'
                : `${visitor.sessionCount} conversations`}
            </Row>
            {visitor.firstSeenAt && (
              <Row icon={<Clock className="h-3 w-3" />} label="Known since">
                {relativeTime(visitor.firstSeenAt)}
              </Row>
            )}
          </dl>
        )}
      </section>

      <section>
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
          Context
        </h3>
        <dl className="mt-2 space-y-1.5 text-xs">
          <Row icon={<Globe className="h-3 w-3" />} label="Came in on">
            {visitor.referrerUrl ? (
              <span className="break-all text-slate-600 dark:text-slate-300">
                {visitor.referrerUrl}
              </span>
            ) : (
              <span className="text-slate-400">Not recorded</span>
            )}
          </Row>
          <Row icon={<span className="text-[10px]">Aa</span>} label="Language">
            {visitor.locale ?? <span className="text-slate-400">Not reported</span>}
          </Row>
        </dl>
      </section>

      {escalations.length > 0 && (
        <section>
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
            Requests for a person
          </h3>
          <ul className="mt-2 space-y-2">
            {escalations.map((e) => (
              <li
                key={e.id}
                className="rounded-lg border border-slate-200 p-2 text-xs dark:border-slate-700"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-700 dark:text-slate-200">
                    {e.reason.replace(/_/g, ' ')}
                  </span>
                  <span className="text-[10px] text-slate-400">{relativeTime(e.createdAt)}</span>
                </div>
                {e.summary && (
                  <p className="mt-1 text-slate-500 dark:text-slate-400">{e.summary}</p>
                )}
                {/* Filled by the offline contact form, and until now read by nothing. */}
                {(e.contactEmail || e.contactPhone) && (
                  <p className="mt-1 text-slate-500 dark:text-slate-400">
                    Left {e.contactEmail ?? ''}
                    {e.contactEmail && e.contactPhone ? ' · ' : ''}
                    {e.contactPhone ?? ''}
                  </p>
                )}
                <p className="mt-1 text-[10px] text-slate-400">
                  {e.status}
                  {e.assignedToName ? ` · ${e.assignedToName}` : ''}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {visitor.previousSessions.length > 0 && (
        <section>
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
            Earlier conversations
          </h3>
          <ul className="mt-2 space-y-1">
            {visitor.previousSessions.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => onOpenSession(s.id)}
                  className="w-full rounded-lg px-2 py-1.5 text-left text-xs hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <span className="text-slate-700 dark:text-slate-200">
                    {s.messageCount} message{s.messageCount === 1 ? '' : 's'}
                  </span>
                  <span className="ml-2 text-slate-400">{relativeTime(s.lastActivityAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Row({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-2">
      <span className="mt-0.5 flex h-3 w-3 shrink-0 items-center justify-center text-slate-400">
        {icon}
      </span>
      <div className="min-w-0">
        <dt className="sr-only">{label}</dt>
        <dd className="text-slate-600 dark:text-slate-300">{children}</dd>
      </div>
    </div>
  );
}
